// [guanxing patch] M-SW63：导出成图右上角的公历日期印章（纯逻辑 + 一格画布绘制）。
//
// 用户需求：「三张生成的截图 右上角角落 加公历日期 格式为 1900-2-24 要很小很小 贴边」。
// 三张图 = 星图图1（西方星座连线）/ 星图图2（中国星官连线）/ 太阳系俯视截图，
// 共用本模块：image-footer.js 在那一次「加页脚」的画布合成里顺带盖这枚日期，故
// 不新增画布、不新增一次 3200² 的 PNG 编码（iOS 内存余量宝贵，M-SW57/58 的教训）。
//
// ── 定案（AskUserQuestion，2026-10-14）────────────────────────────────
// ① 日期取值 = **画面同一时刻**：星图读引擎 core.observer.utc，太阳系读同源桥
//    getTime()。出生信息提交后引擎冻结在出生那一刻，故默认即出生日期；把时间
//    拖到别的日子后随之改变——画面与日期永不自相矛盾。
// ② 时区 = **中国民用时（固定 UTC+8）**。原定案是「设备本地时区」，M-SW64 实测暴露
//    了口径分裂：出生地全部为中国城市（birth-overlay 按 +08:00 解析），同一瞬间在
//    GMT 民用日里是前一天下午 16:00 —— 太阳系子站按 UTC 显示就退回前一天（用户实测：
//    输入 2006-10-18 00:00，操作栏显示 2006年10月17日）。改为固定 +08:00 后，印章与
//    太阳系操作栏**同时**显示 2006-10-18，且与设备时区无关；顺带免掉 IANA 历史规则
//    （Asia/Shanghai 在 1900 年是 +08:05:43，不是 +08:00）带来的远古年份漂移。
// ③ 格式 = **纯数字不补零**，形如 `1900-2-24` / `1900-1-9`；不写「公历」字样。
// ④ 大小 = 图宽 × 0.45%（1600→7px、2400→11px、3200→14px），距上/右边各
//    × 0.35%（1600→6px），白色 32% 不透明，右对齐贴角。三张图同一套比例，
//    故观感一致（桌面 3200 图自动等比放大）。
//
// 引擎时间口径：Stellarium 内部 observer.utc 是 **MJD**（App.vue 用
// `m.toDate().getMJD()` 赋值、solar-system-dialog 用 `J2000_MJD + t` 写回，
// 两处都印证是 MJD）。太阳系桥 getTime() 是 **J2000 起算天数**。

export const UNIX_EPOCH_MJD = 40587
export const J2000_MJD = 51544.5

const MS_PER_DAY = 86400000

// MJD（Stellarium observer.utc 口径）→ Unix 毫秒。
export function mjdToUnixMs (mjd) {
  if (!Number.isFinite(mjd)) return NaN
  return (mjd - UNIX_EPOCH_MJD) * MS_PER_DAY
}

// 太阳系桥 getTime() 的「J2000 起算天数」→ Unix 毫秒。
export function j2000DaysToUnixMs (days) {
  if (!Number.isFinite(days)) return NaN
  return mjdToUnixMs(days + J2000_MJD)
}

// 中国民用时的固定偏移（UTC+8）。出生地全为中国城市，故日期一律按此呈现（M-SW64）。
export const CN_OFFSET_MS = 8 * 3600000

// 公历日期文本，固定 `YYYY-M-D`（不补零）。按**中国民用时**取「瞬时 + 8h 后的 UTC 分量」
// ——固定偏移，不读设备时区，故同一张成图在任何设备上日期一致；无法换算时返回 ''
// （调用方据此跳过印章，不产出「空日期」）。
// 边界诚实：年份为负或小于 1000 时按该数字原样输出（太阳系仪可漫游到远古），
// 本项目实际入口（出生信息 1900–当前年）落在 1900+ 区间。
export function civilDateText (ms) {
  if (!Number.isFinite(ms)) return ''
  const d = new Date(ms + CN_OFFSET_MS)
  if (Number.isNaN(d.getTime())) return ''
  return d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate()
}

// 印章档位（比例而非像素，保证三张不同分辨率的成图观感一致）。
export const STAMP_FONT_RATIO = 0.0045
export const STAMP_MARGIN_RATIO = 0.0035
export const STAMP_ALPHA = 0.32
export const STAMP_COLOR = '#ffffff'
// 数字与连字符足够，用系统 UI 字栈；与页脚/对话框文案的字体族保持同源。
export const STAMP_FONT_FAMILY = '-apple-system, "Segoe UI", system-ui, sans-serif'

// 给定成图宽度，算出印章的三个像素量（取整，避免亚像素糊字）。
export function stampMetrics (imageWidth) {
  const w = Number.isFinite(imageWidth) && imageWidth > 0 ? imageWidth : 1600
  return {
    fontPx: Math.max(1, Math.round(w * STAMP_FONT_RATIO)),
    marginPx: Math.max(1, Math.round(w * STAMP_MARGIN_RATIO)),
    alpha: STAMP_ALPHA
  }
}

// 在成图右上角盖日期。ctx 已画好截图（可选的页脚 banner 亦已就位）。
// 印章属装饰件：任何异常只记 warn 并跳过，绝不抛出（不得把导出搞挂）。
export function drawDateStamp (ctx, imageWidth, ms) {
  try {
    const text = civilDateText(ms)
    if (!text || !ctx) return
    const m = stampMetrics(imageWidth)
    ctx.save()
    ctx.globalAlpha = m.alpha
    ctx.fillStyle = STAMP_COLOR
    ctx.textAlign = 'right'
    ctx.textBaseline = 'top'
    ctx.font = '400 ' + m.fontPx + 'px ' + STAMP_FONT_FAMILY
    ctx.fillText(text, imageWidth - m.marginPx, m.marginPx)
    ctx.restore()
  } catch (e) {
    console.warn('[M-SW63] 日期印章绘制失败，跳过（不影响成图）:', e)
  }
}
